import {
  toWSDateString,
  useWSSnackbar,
  WSAvatar,
  WSButton,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSTable,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { getPathnameMyResourcesTab, PATH_MY_RESOURCES } from "../paths";
import {
  DataViewGrid,
  DataViewSection
} from "../../../shared/components/DataView";
import { WSQueries } from "../../../query/WSQuery";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import type { insuranceCoverageApiSpec } from "@wingspanhq/wingspan-api-types";
import { useHistory } from "react-router-dom";
import { MyResourcesTab } from "../types";
import { useQueryInsuranceImport } from "../../../query/insuranceImport/queries/useQueryInsuranceImport";
import { selectorPolicyTypeByPolicyType } from "../selectors/selectorPolicyTypeByPolicyType";
import { MyResourceInsuranceImportHistory } from "../components/MyResourceInsuranceImportHistory";
import { mapInsuranceCoverageToDataView } from "../../Requirements/modules/SharedFile/configs/helpers/mapInsuranceCoverageToDataView";
import { mapInsuranceLimitsToTableData } from "../../Requirements/modules/SharedFile/configs/helpers/mapInsuranceLimitsToTableData";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { useMutationDownloadPrivateDocument } from "../../../query/files/mutations";
import { filesService } from "../../../services/files";
import { downloadFileFromBuffer } from "../../../utils/files";
import { useRequirementDefaultActions } from "../../Requirements/hooks/useRequirementDefaultActions";
import { SHARED_FILE_REQUIREMENT_EVENT } from "../../Requirements/modules/SharedFile/configs/utils";
import { RequirementContextType } from "../../Requirements/types";

type Props = {
  insuranceCoverage: insuranceCoverageApiSpec.InsuranceCoverage;
  tab?: MyResourcesTab;
};

export const InsuranceImportDetails: React.FC<Props> = ({
  insuranceCoverage,
  tab
}) => {
  const history = useHistory();
  const qInsuranceImport = useQueryInsuranceImport(insuranceCoverage.sourceId);

  const now = useGetNow();
  const { openSnackbar } = useWSSnackbar();
  const [downloadDocument] = useMutationDownloadPrivateDocument();

  return (
    <WSPage colorBackground="gray50">
      <WSQueries queries={{ qInsuranceImport }}>
        {({ qInsuranceImportData }) => {
          const name = insuranceCoverage?.title;

          const handleSourceDownload = async (fileId: string) => {
            const file = await filesService.member.private.get(fileId);

            await downloadDocument(file.fileId, {
              onSuccess: blob => {
                downloadFileFromBuffer(blob, file?.filename, file.mimetype);
                openSnackbar({
                  message: "File downloaded successfully",
                  type: "success"
                });
              },
              onError: () => {
                openSnackbar({
                  message: "Download failed",
                  type: "warning"
                });
              }
            });
          };

          return (
            <>
              <WSPageToolbar
                title={name}
                actions={[
                  {
                    label: "View document",
                    buttonKind: "Primary",
                    onClick: () => {
                      handleSourceDownload(
                        qInsuranceImportData.fileId as string
                      );
                    }
                  }
                ]}
                breadcrumbs={[
                  {
                    label: "My resources",
                    onClick: () => {
                      if (tab) {
                        return history.push(getPathnameMyResourcesTab(tab));
                      } else {
                        history.push(PATH_MY_RESOURCES);
                      }
                    }
                  },
                  {
                    label: selectorPolicyTypeByPolicyType(
                      insuranceCoverage.policyType
                    )
                  }
                ]}
              />
              <WSGrid>
                <WSGrid.Item span={{ s: "12", m: "8" }}>
                  <WSList gap="XL">
                    <DataViewSection
                      gridColumns={3}
                      title="Policy information"
                      data={[
                        {
                          label: "Insurer",
                          value: insuranceCoverage.insurerName
                        },
                        {
                          label: "Policy number",
                          value: insuranceCoverage.policyNumber
                        },
                        {
                          label: "Status",
                          pill: selectorInsuranceCoverageStatusPillByExpirationDate(
                            insuranceCoverage.expirationDate
                          ),
                          value: null,
                          hideValue: true,
                          secondaryText:
                            selectorInsuranceCoverageStatusDescriptionByInsurance(
                              insuranceCoverage,
                              now
                            )
                        }
                      ]}
                    ></DataViewSection>
                    <WSPanel>
                      <DataViewGrid
                        data={mapInsuranceCoverageToDataView(
                          insuranceCoverage as any,
                          { showBasicInfo: false, showLimits: false }
                        )}
                        mb="L"
                      />
                      <WSText.ParagraphSm weight="medium" mb="S">
                        Limits
                      </WSText.ParagraphSm>
                      <WSTable<any>
                        tableData={mapInsuranceLimitsToTableData(
                          insuranceCoverage
                        )}
                        columns={[
                          {
                            config: {
                              gridTemplateSizeMax: "999px"
                            },
                            renderFunction: ({ data }) => (
                              <WSText.ParagraphSm weight="medium">
                                {data.label}
                              </WSText.ParagraphSm>
                            )
                          },
                          {
                            config: {
                              gridTemplateSizeMax: "999px"
                            },
                            renderFunction: ({ data }) => (
                              <WSText.ParagraphXs weight="light">
                                {data.value}
                              </WSText.ParagraphXs>
                            )
                          }
                        ]}
                      />
                    </WSPanel>
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ s: "12", m: "4" }}>
                  <WSList gap="XL">
                    <WSElement>
                      <WSSectionToolbar title="Source" />
                      <WSCard
                        mt="S"
                        divider
                        actions={{
                          alignment: "fill",
                          buttons: [
                            {
                              label: "View document",
                              kind: "Secondary",
                              onClick: () => {
                                handleSourceDownload(
                                  qInsuranceImportData.fileId as string
                                );
                              }
                            }
                          ]
                        }}
                      >
                        <WSList gap="L">
                          <WSFlexBox.CenterY gap="M" wrap="nowrap">
                            <WSAvatar.Icon
                              icon="upload"
                              colorBackground="gray500"
                            />
                            <WSText.Paragraph weight="medium">
                              {name}
                            </WSText.Paragraph>
                          </WSFlexBox.CenterY>

                          <WSInfoBox>
                            This policy was extracted and imported from an
                            insurance document uploaded by you on{" "}
                            {toWSDateString(insuranceCoverage.createdAt)}
                          </WSInfoBox>
                        </WSList>
                      </WSCard>
                    </WSElement>
                    <MyResourceInsuranceImportHistory
                      insuranceImportId={qInsuranceImportData.insuranceImportId}
                      isCompactView={true}
                      insuranceCoverageId={
                        insuranceCoverage.insuranceCoverageId
                      }
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
